/**
 * Live Fleet Map page (Wave 2.7 + W2.12 responsive/a11y).
 * MapLibre only via `@/components/pmv/map`. List fallback always usable.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Descriptions,
  Grid,
  Input,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import { FleetMap } from '@/components/pmv/map';
import type { FleetMapMarkerInput } from '@/components/pmv/map';
import pmvService from '@/services/pmv.service';
import type { PmvFleetMapMarker } from '@/services/pmv.service';
import { connectivityLabel, connectivityTagColor, formatLastSeen } from '@/utils/pmvConnectedCopy';
import {
  fleetMapEmptyMessage,
  PMV_MAP_ERROR,
  PMV_MAP_LEGEND,
} from '@/utils/pmvMapUxCopy';

const { Text } = Typography;
const { useBreakpoint } = Grid;

function freshnessTag(f: string | null | undefined) {
  switch (f) {
    case 'LIVE':
      return <Tag color="success">Live position</Tag>;
    case 'DELAYED':
      return <Tag color="warning">Delayed</Tag>;
    case 'LAST_KNOWN':
      return <Tag>Last known</Tag>;
    default:
      return <Tag>No position</Tag>;
  }
}

export const PmvFleetMapPage: React.FC = () => {
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const isNarrow = !screens.lg;
  const [searchParams, setSearchParams] = useSearchParams();

  const [q, setQ] = useState(() => searchParams.get('q') || '');
  const [connectivity, setConnectivity] = useState<string | undefined>(
    () => searchParams.get('connectivity') || undefined
  );
  const [movement, setMovement] = useState<string | undefined>(
    () => searchParams.get('movement') || undefined
  );
  const [attentionOnly, setAttentionOnly] = useState(
    () => searchParams.get('attention') === '1'
  );
  const [includeStale, setIncludeStale] = useState(
    () => searchParams.get('stale') === '1'
  );
  const [projectId, setProjectId] = useState<string | undefined>(
    () => searchParams.get('project') || undefined
  );
  const [showGeofences, setShowGeofences] = useState(true);
  const [satellite, setSatellite] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(
    () => searchParams.get('equipment') || null
  );
  const [fitToken, setFitToken] = useState(0);
  const [bbox, setBbox] = useState<{
    min_lat: number;
    max_lat: number;
    min_lon: number;
    max_lon: number;
  } | null>(null);
  const [boundsFilter, setBoundsFilter] = useState(false);

  const hasSatelliteStyle = Boolean(
    (import.meta.env.VITE_PMV_MAP_SATELLITE_STYLE_URL as string | undefined)?.trim()
  );

  // Sync deep-link params (equipment / attention / project) without huge geometry
  useEffect(() => {
    const next = new URLSearchParams();
    if (selectedId) next.set('equipment', selectedId);
    if (attentionOnly) next.set('attention', '1');
    if (includeStale) next.set('stale', '1');
    if (projectId) next.set('project', projectId);
    if (connectivity) next.set('connectivity', connectivity);
    if (movement) next.set('movement', movement);
    if (q) next.set('q', q);
    setSearchParams(next, { replace: true });
  }, [
    selectedId,
    attentionOnly,
    includeStale,
    projectId,
    connectivity,
    movement,
    q,
    setSearchParams,
  ]);

  const fleetQ = useQuery({
    queryKey: [
      'pmv-fleet-map',
      q,
      connectivity,
      movement,
      attentionOnly,
      includeStale,
      projectId,
      boundsFilter ? bbox : null,
    ],
    queryFn: () =>
      pmvService.getFleetMap({
        q: q || undefined,
        connectivity,
        movement_state: movement,
        attention_only: attentionOnly || undefined,
        include_stale: includeStale || undefined,
        project_id: projectId,
        ...(boundsFilter && bbox
          ? {
              min_lat: bbox.min_lat,
              max_lat: bbox.max_lat,
              min_lon: bbox.min_lon,
              max_lon: bbox.max_lon,
              cluster: true,
            }
          : {}),
      }),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

  const geofencesQ = useQuery({
    queryKey: ['pmv-map-geofences', projectId],
    queryFn: () => pmvService.getMapGeofences({ project_id: projectId }),
    enabled: showGeofences,
  });

  const cardQ = useQuery({
    queryKey: ['pmv-fleet-map-card', selectedId],
    queryFn: () => pmvService.getFleetMapCard(selectedId!),
    enabled: Boolean(selectedId),
  });

  const data = fleetQ.data;
  const list = data?.list ?? [];
  const markers = data?.markers ?? [];

  const mapMarkers: FleetMapMarkerInput[] = useMemo(
    () =>
      markers
        .filter((m): m is PmvFleetMapMarker & { location: NonNullable<PmvFleetMapMarker['location']> } =>
          Boolean(m.location)
        )
        .map((m) => ({
          id: m.pmv_profile_id,
          lat: m.location.lat,
          lon: m.location.lon,
          label: m.fleet_number || m.asset_code || m.asset_name || m.pmv_profile_id,
          freshness: m.location.freshness,
          connectivity: m.connectivity,
          movement: m.movement_state,
          hasAttention: m.attention.open_count > 0,
        })),
    [markers]
  );

  const selectedRow = list.find((r) => r.pmv_profile_id === selectedId) ?? null;
  const hasFilters = Boolean(q || connectivity || movement || attentionOnly || projectId || boundsFilter);
  const emptyMsg = fleetMapEmptyMessage({
    isError: fleetQ.isError,
    listRows: list.length,
    hasFilters,
    liveCount: data?.summary.live,
    delayedCount: data?.summary.delayed,
  });

  const mapHeight = isNarrow ? 320 : 560;

  const selectEquipment = (id: string) => {
    setSelectedId(id);
  };

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel="Live Fleet Map"
          documentNumber=""
          statusTag={<Tag color="blue">Wave 2</Tag>}
          subtitle={
            <Text type="secondary">
              Location is observed evidence only — never silently changes allocation or ops status. Map outage does
              not block the equipment list.
            </Text>
          }
          primaryActions={
            <Space wrap>
              <Button onClick={() => setFitToken((n) => n + 1)}>Fit fleet</Button>
              <Button onClick={() => void fleetQ.refetch()}>Refresh</Button>
              <Button onClick={() => navigate('/asset/pmv/connected/movement-attention')}>
                Spatial attention
              </Button>
              <Button onClick={() => navigate('/asset/pmv/connected')}>Connected overview</Button>
              <Button onClick={() => navigate('/asset/pmv/connected/geofences')}>Geofences</Button>
            </Space>
          }
        />
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {fleetQ.isError ? (
          <Alert
            type="error"
            showIcon
            message="Could not load fleet map data"
            description={
              (fleetQ.error as Error)?.message || PMV_MAP_ERROR.dataLoadFailed
            }
          />
        ) : null}
        {mapError ? (
          <Alert
            type="warning"
            showIcon
            closable
            onClose={() => setMapError(null)}
            message="Map imagery unavailable"
            description={PMV_MAP_ERROR.imageryUnavailable}
          />
        ) : null}
        {emptyMsg ? (
          <Alert type="info" showIcon message={emptyMsg} />
        ) : null}

        <Card size="small" title="Filters">
          <Space wrap style={{ width: '100%' }} role="search" aria-label="Fleet map filters">
            <Input.Search
              allowClear
              placeholder="Search fleet / asset"
              style={{ width: isNarrow ? '100%' : 220, maxWidth: 320 }}
              defaultValue={q}
              onSearch={setQ}
              onChange={(e) => {
                if (!e.target.value) setQ('');
              }}
              aria-label="Search equipment"
            />
            <Input
              allowClear
              placeholder="Project id (optional)"
              style={{ width: isNarrow ? '100%' : 200, maxWidth: 280 }}
              value={projectId}
              onChange={(e) => setProjectId(e.target.value || undefined)}
              aria-label="Filter by project id"
            />
            <Select
              allowClear
              placeholder="Connectivity"
              style={{ width: 140 }}
              value={connectivity}
              options={[
                { value: 'ONLINE', label: 'Live' },
                { value: 'DELAYED', label: 'Delayed' },
                { value: 'OFFLINE', label: 'Offline' },
              ]}
              onChange={(v) => setConnectivity(v)}
              aria-label="Connectivity filter"
            />
            <Select
              allowClear
              placeholder="Movement"
              style={{ width: 140 }}
              value={movement}
              options={[
                { value: 'MOVING', label: 'Moving' },
                { value: 'STATIONARY', label: 'Stationary' },
                { value: 'UNKNOWN', label: 'Unknown' },
              ]}
              onChange={(v) => setMovement(v)}
              aria-label="Movement filter"
            />
            <Checkbox
              checked={attentionOnly}
              onChange={(e) => setAttentionOnly(e.target.checked)}
            >
              Attention only
            </Checkbox>
            <Checkbox checked={includeStale} onChange={(e) => setIncludeStale(e.target.checked)}>
              Show last-known
            </Checkbox>
            <Checkbox checked={boundsFilter} onChange={(e) => setBoundsFilter(e.target.checked)}>
              Filter by map viewport
            </Checkbox>
            <Checkbox checked={showGeofences} onChange={(e) => setShowGeofences(e.target.checked)}>
              Geofences
            </Checkbox>
            {hasSatelliteStyle ? (
              <Checkbox checked={satellite} onChange={(e) => setSatellite(e.target.checked)}>
                Satellite
              </Checkbox>
            ) : null}
            <Text type="secondary">
              Markers {data?.summary.map_markers ?? '—'} · List {data?.summary.list_rows ?? '—'} · Attention{' '}
              {data?.summary.with_attention ?? '—'}
            </Text>
          </Space>
        </Card>

        <Card size="small" title="Legend">
          <Space wrap size={[8, 8]} aria-label="Map legend">
            {PMV_MAP_LEGEND.map((item) => (
              <Tag key={item.key} title={item.hint}>
                {item.label}
              </Tag>
            ))}
            <Text type="secondary" style={{ fontSize: 12 }}>
              Geofences: project / site / work-front / restricted (geometry may be redacted).
            </Text>
          </Space>
        </Card>

        <Row gutter={[12, 12]}>
          {/* List first on narrow viewports for a11y / low bandwidth */}
          <Col xs={24} lg={8} xl={7} order={isNarrow ? 0 : 1}>
            <Card
              size="small"
              title="Equipment list"
              loading={fleetQ.isLoading}
              styles={{ body: { padding: 0 } }}
              extra={
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Mandatory list fallback
                </Text>
              }
            >
              <Table<PmvFleetMapMarker>
                size="small"
                rowKey="pmv_profile_id"
                pagination={{ pageSize: isNarrow ? 8 : 12, size: 'small' }}
                dataSource={list}
                // scroll.y without scroll.x + column widths collapses the Unit cell
                // (character-per-line wrap). Keep a min table width in the sidebar.
                scroll={{ y: isNarrow ? 280 : 360, x: 720 }}
                tableLayout="fixed"
                locale={{ emptyText: emptyMsg || 'No equipment' }}
                rowClassName={(row) =>
                  row.pmv_profile_id === selectedId ? 'pmv-fleet-map-row-selected' : ''
                }
                onRow={(row) => ({
                  onClick: () => selectEquipment(row.pmv_profile_id),
                  onKeyDown: (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      selectEquipment(row.pmv_profile_id);
                    }
                  },
                  tabIndex: 0,
                  role: 'button',
                  'aria-selected': row.pmv_profile_id === selectedId,
                  'aria-label': `Select ${row.fleet_number || row.asset_code || row.pmv_profile_id}`,
                  style: {
                    cursor: 'pointer',
                    background:
                      row.pmv_profile_id === selectedId ? 'rgba(22, 119, 255, 0.08)' : undefined,
                  },
                })}
                columns={[
                  {
                    title: 'Unit',
                    key: 'unit',
                    width: 168,
                    fixed: 'left',
                    ellipsis: true,
                    render: (_, r) => {
                      const code = r.fleet_number || r.asset_code || r.pmv_profile_id.slice(0, 8);
                      return (
                        <div style={{ minWidth: 0, maxWidth: '100%' }}>
                          <div
                            style={{
                              fontWeight: 500,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                            title={code}
                          >
                            {code}
                          </div>
                          <Text
                            type="secondary"
                            style={{
                              fontSize: 12,
                              display: 'block',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                            title={r.asset_name || undefined}
                          >
                            {r.asset_name || '—'}
                          </Text>
                        </div>
                      );
                    },
                  },
                  {
                    title: 'Ops',
                    dataIndex: 'operational_status',
                    width: 108,
                    ellipsis: true,
                  },
                  {
                    title: 'Conn',
                    width: 88,
                    render: (_, r) => (
                      <Tag color={connectivityTagColor(r.connectivity)}>
                        {connectivityLabel(r.connectivity)}
                      </Tag>
                    ),
                  },
                  {
                    title: 'Move',
                    dataIndex: 'movement_state',
                    width: 96,
                    ellipsis: true,
                  },
                  {
                    title: 'Observed',
                    width: 110,
                    ellipsis: true,
                    render: (_, r) =>
                      r.observed.site_refs[0] ||
                      r.observed.work_front_refs[0] ||
                      r.observed.project_ids[0]?.slice(0, 8) ||
                      (r.observed.restricted ? 'Restricted' : '—'),
                  },
                  {
                    title: 'Fresh',
                    width: 100,
                    render: (_, r) => freshnessTag(r.location?.freshness),
                  },
                  {
                    title: 'Attn',
                    width: 56,
                    render: (_, r) =>
                      r.attention.open_count > 0 ? (
                        <Tag color="error">{r.attention.open_count}</Tag>
                      ) : (
                        '—'
                      ),
                  },
                ]}
              />
            </Card>

            <Card
              size="small"
              title="Selection"
              style={{ marginTop: 12 }}
              loading={cardQ.isLoading}
              id="pmv-fleet-selection"
            >
              {!selectedRow ? (
                <Text type="secondary">Select a unit from the list or map.</Text>
              ) : (
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Descriptions size="small" column={1}>
                    <Descriptions.Item label="Unit">
                      <Space direction="vertical" size={0}>
                        <Text>
                          {selectedRow.fleet_number || selectedRow.asset_code || selectedRow.pmv_profile_id}
                        </Text>
                        {selectedRow.asset_name ? (
                          <Text type="secondary">{selectedRow.asset_name}</Text>
                        ) : null}
                      </Space>
                    </Descriptions.Item>
                    <Descriptions.Item label="Ops">{selectedRow.operational_status}</Descriptions.Item>
                    <Descriptions.Item label="Connectivity">
                      <Tag color={connectivityTagColor(selectedRow.connectivity)}>
                        {connectivityLabel(selectedRow.connectivity)}
                      </Tag>{' '}
                      {selectedRow.location?.observed_at
                        ? formatLastSeen(selectedRow.location.observed_at)
                        : ''}
                    </Descriptions.Item>
                    <Descriptions.Item label="Movement">{selectedRow.movement_state}</Descriptions.Item>
                    <Descriptions.Item label="Position">
                      {freshnessTag(selectedRow.location?.freshness)}
                      {selectedRow.location?.freshness === 'LAST_KNOWN' ? (
                        <div>
                          <Text type="secondary">Last known — not confident current presence</Text>
                        </div>
                      ) : null}
                    </Descriptions.Item>
                    <Descriptions.Item label="Expected">
                      {selectedRow.expected.project_id || 'No active allocation'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Observed">
                      {selectedRow.observed.site_refs[0] ||
                        selectedRow.observed.work_front_refs[0] ||
                        selectedRow.observed.project_ids[0] ||
                        (selectedRow.observed.restricted ? 'Restricted' : '—')}
                    </Descriptions.Item>
                    <Descriptions.Item label="Attention">
                      {selectedRow.attention.types.length
                        ? selectedRow.attention.types.join(', ')
                        : 'None'}
                    </Descriptions.Item>
                  </Descriptions>
                  <Space wrap>
                    <Button
                      type="primary"
                      onClick={() => navigate(`/asset/pmv/${selectedRow.pmv_profile_id}`)}
                    >
                      Open Passport
                    </Button>
                    <Button
                      onClick={() => navigate('/asset/pmv/connected/movement-attention')}
                    >
                      Spatial attention
                    </Button>
                  </Space>
                  {cardQ.data?.card ? (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {cardQ.data.card.last_position_label}
                    </Text>
                  ) : null}
                </Space>
              )}
            </Card>
          </Col>

          <Col xs={24} lg={16} xl={17} order={isNarrow ? 1 : 2}>
            <Card
              size="small"
              title="Map"
              extra={
                isNarrow ? (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Scroll to list below if imagery fails
                  </Text>
                ) : null
              }
            >
              <FleetMap
                key={String(satellite)}
                markers={mapMarkers}
                geofences={geofencesQ.data ?? []}
                selectedId={selectedId}
                showGeofences={showGeofences}
                satellite={satellite}
                fitToken={fitToken}
                height={mapHeight}
                onSelect={selectEquipment}
                onMapError={setMapError}
                onViewportIdle={(b) => {
                  setBbox(b);
                }}
              />
              <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
                List ↔ map stay synchronized. Clustering on zoom-out. Viewport filter uses bounds +
                cluster for large fleets. Public VITE map style tokens only — never OEM secrets.
              </Text>
            </Card>
          </Col>
        </Row>
      </Space>
    </WorkspaceLayout>
  );
};

export default PmvFleetMapPage;
